import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Container,
  Group,
  Title,
  Button,
  SimpleGrid,
  Card,
  Text,
  ActionIcon,
  Modal,
  Select,
  Stack,
  Loader,
  Center,
  Tooltip,
  Badge,
  SegmentedControl,
  Alert,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import {
  IconPlus,
  IconTrash,
  IconArrowLeft,
  IconNotebook,
  IconDownload,
  IconInfoCircle,
} from '@tabler/icons-react';
import {
  useMyEssays,
  useCreateEssay,
  useEssay,
  useDeleteEssay,
  useExportEssayDocx,
  useSubmitEssay,
  useUnsubmitEssay,
  EssaySummary,
  essayErrorMessage,
} from '../../hooks/useEssayApi';
import { EssayEditor } from '../../components/essays/EssayEditor';
import { CopyEssayPromptButton } from '../../components/essays/CopyEssayPromptButton';
import { monthLabel, reportingPeriod } from '../../components/essays/essayPeriod';
import { QuarterlyTab } from './QuarterlyTab';
import { showToast } from '../../utils/toast';
import { ApiError } from '../../utils/api';
import { useCompanyModules } from '../../hooks/useCompanyModules';

export const Essays: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const createEssay = useCreateEssay();
  const deleteEssay = useDeleteEssay();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalOpened, { open, close }] = useDisclosure(false);
  // Месячное и квартальное для человека — одно и то же «эссе», поэтому
  // переключатель, а не отдельный пункт меню.
  // Режим в адресе: ссылки «к эссе» и «к квартальному отчёту» открывают нужную вкладку.
  const [searchParams, setSearchParams] = useSearchParams();
  // Подключён один из двух разделов — его вкладка без переключателя (`Tenant.modules`).
  const { hasModule } = useCompanyModules();
  const hasBoth = hasModule('essays') && hasModule('quarterly');
  // Компания «только квартальные» месячных эссе не запрашивает.
  const { data: essays, isLoading } = useMyEssays(hasModule('essays'));
  const mode =
    !hasModule('essays') || (hasModule('quarterly') && searchParams.get('mode') === 'quarter')
      ? 'quarter'
      : 'month';
  const setMode = (next: 'month' | 'quarter') =>
    setSearchParams(next === 'quarter' ? { mode: 'quarter' } : {}, { replace: true });
  // Точка в шапке для кнопок вкладки «Квартал» (портал из QuarterlyTab).
  const [headerActions, setHeaderActions] = useState<HTMLDivElement | null>(null);

  const now = dayjs();
  // Не текущий месяц, а отчётный — см. reportingPeriod.
  const [month, setMonth] = useState(() => String(reportingPeriod().month));
  const [year, setYear] = useState(() => String(reportingPeriod().year));

  // Селекты живут вне модалки и переживают её закрытие: выбрал человек другой
  // месяц один раз — и следующее «Добавить эссе» открылось бы на нём, мимо
  // отчётного периода. Ради этого вся правка и делалась, поэтому сбрасываем.
  const openCreate = () => {
    const period = reportingPeriod();
    setMonth(String(period.month));
    setYear(String(period.year));
    open();
  };

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1),
        label: dayjs()
          .locale(lang || 'ru')
          .month(i)
          .format('MMMM'),
      })),
    [lang],
  );
  const yearOptions = useMemo(() => {
    const y = now.year();
    return Array.from({ length: 5 }, (_, i) => String(y - 3 + i));
  }, [now]);

  const handleCreate = () => {
    const y = Number(year);
    const m = Number(month);
    const existing = essays?.find((e) => e.year === y && e.month === m);
    if (existing) {
      setSelectedId(existing._id);
      close();
      return;
    }
    createEssay.mutate(
      { year: y, month: m },
      {
        onSuccess: (created) => {
          setSelectedId(created._id);
          close();
        },
        onError: (error) => {
          // Эссе за месяц уже есть (например, создали в другой вкладке) — открываем его.
          const existingId =
            error instanceof ApiError && error.status === 409
              ? (error.response.data as { id?: string } | null)?.id
              : undefined;
          if (existingId) {
            setSelectedId(existingId);
            close();
            return;
          }
          showToast({
            title: t('common.error'),
            message: essayErrorMessage(t, error, 'essays.saveError'),
            color: 'red',
          });
        },
      },
    );
  };

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    modals.openConfirmModal({
      title: t('essays.confirmDelete'),
      centered: true,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        deleteEssay.mutate(id, {
          onError: (error) =>
            showToast({
              title: t('common.error'),
              message: essayErrorMessage(t, error, 'essays.saveError'),
              color: 'red',
            }),
        }),
    });
  };

  if (selectedId) {
    return <EssayDetail id={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="lg" wrap="wrap">
        <Group gap="xs" align="center">
          <Title order={2}>{t('essays.myEssays')}</Title>
          <Badge size="xs" variant="outline">
            {t('common.beta')}
          </Badge>
        </Group>
        <Group gap="xs">
          {hasBoth && (
            <SegmentedControl
              size="xs"
              value={mode}
              onChange={(v) => setMode(v as 'month' | 'quarter')}
              data={[
                { value: 'month', label: t('quarterly.monthTab') },
                { value: 'quarter', label: t('quarterly.tab') },
              ]}
            />
          )}
          {/* Сюда вкладка «Квартал» выносит свои кнопки — на то же место, что у месяца. */}
          {mode === 'quarter' && <Group gap="xs" ref={setHeaderActions} />}
          {mode === 'month' && (
            <>
              <CopyEssayPromptButton />
              <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
                {t('essays.add')}
              </Button>
            </>
          )}
        </Group>
      </Group>

      {mode === 'quarter' && <QuarterlyTab headerActions={headerActions} />}

      {mode === 'month' &&
        (isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : !essays || essays.length === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="sm">
              <IconNotebook size={48} opacity={0.4} />
              <Text c="dimmed">{t('essays.emptyList')}</Text>
              <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
                {t('essays.add')}
              </Button>
            </Stack>
          </Center>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {essays.map((essay: EssaySummary) => (
              <Card
                key={essay._id}
                withBorder
                radius="md"
                padding="md"
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedId(essay._id)}
              >
                <Group justify="space-between" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap">
                    <IconNotebook size={20} />
                    <Text fw={600} tt="capitalize">
                      {monthLabel(essay.year, essay.month, lang)}
                    </Text>
                  </Group>
                  {/* Сданное своё не удалить (сервер ответит 403) — корзину не показываем. */}
                  {essay.status !== 'submitted' && (
                    <Tooltip label={t('common.delete')} withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        aria-label={t('common.delete')}
                        onClick={(e) => handleDelete(e, essay._id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
                <Text size="xs" c="dimmed" mt="xs">
                  {t('essays.updated')}:{' '}
                  {dayjs(essay.updatedAt)
                    .locale(lang || 'ru')
                    .format('D MMM YYYY, HH:mm')}
                </Text>
                {essay.returnedForRework && essay.status !== 'submitted' ? (
                  <Badge mt="sm" variant="light" color="orange">
                    {t('rework.summaryStatus')}
                  </Badge>
                ) : (
                  <Badge
                    mt="sm"
                    variant="light"
                    color={essay.status === 'submitted' ? 'teal' : 'gray'}
                  >
                    {t(`essays.status.${essay.status || 'draft'}`)}
                  </Badge>
                )}
              </Card>
            ))}
          </SimpleGrid>
        ))}

      <Modal opened={modalOpened} onClose={close} title={t('essays.newEssay')} centered>
        <Stack>
          <Select
            label={t('essays.month')}
            data={monthOptions}
            value={month}
            onChange={(v) => v && setMonth(v)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
          <Select
            label={t('essays.year')}
            data={yearOptions}
            value={year}
            onChange={(v) => v && setYear(v)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
          <Button onClick={handleCreate} loading={createEssay.isPending} fullWidth>
            {t('common.create')}
          </Button>
        </Stack>
      </Modal>
    </Container>
  );
};

const EssayDetail: React.FC<{ id: string; onBack: () => void }> = ({ id, onBack }) => {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useEssay(id);
  const { exportEssay, isExporting } = useExportEssayDocx();
  // Ошибку смены статуса показывает сам хук — второй тост здесь не нужен.
  const submitEssay = useSubmitEssay();
  const unsubmitEssay = useUnsubmitEssay();

  return (
    <Container size="lg" py="md">
      <Group mb="md" justify="space-between">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          {t('common.back')}
        </Button>
        {data && (
          <Button
            variant="light"
            color="teal"
            leftSection={<IconDownload size={16} />}
            onClick={() => exportEssay(id)}
            loading={isExporting}
          >
            {t('essays.exportDocx')}
          </Button>
        )}
      </Group>
      {error ? (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
          {essayErrorMessage(t, error, 'essays.errors.notFound')}
        </Alert>
      ) : isLoading || !data ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <>
          <Title order={2} mb="md" tt="capitalize">
            {monthLabel(data.essay.year, data.essay.month, i18n.language)}
          </Title>
          <EssayEditor
            // Смена статуса меняет версию эссе — редактор пересобирается на новой.
            key={`${data.essay._id}:${data.essay.status}`}
            essayId={data.essay._id}
            initialContent={data.essay}
            readOnly={!data.canEdit}
            status={data.essay.status}
            importedAt={data.essay.importedAt}
            updatedAt={data.essay.updatedAt}
            returnedForRework={data.essay.returnedForRework}
            canSubmit
            isStatusPending={submitEssay.isPending || unsubmitEssay.isPending}
            onSubmit={() => submitEssay.mutate(id)}
            onUnsubmit={() => unsubmitEssay.mutate(id)}
          />
        </>
      )}
    </Container>
  );
};
